import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-button-group>` says about itself.
 *
 * The examples are chosen to answer the question this piece actually raises, which is not "what does
 * a joined run look like" but "when is this the right thing and when is it one of the three pieces
 * that look like it". So there is an example of a group of toggles beside an example of a plain run
 * of actions, and the `insteadUse` list names all three neighbours rather than the nearest one.
 */
const docs: ComponentDocs = {
  // The same places a button goes, for the same reason: this is several buttons. It is not on a
  // wizard step, where one button moves the flow on and a joined run of alternatives beside it is a
  // choice the step should have made in its own layout.
  placements: ['recordPage', 'homePage', 'appPage', 'recordAction', 'seam'],

  inputs: [
    {
      name: 'size',
      attribute: 'size',
      type: 'text',
      options: ['sm', 'md', 'lg'],
      description:
        'Control height from the platform size scale — sm, md or lg. Every button in the group is ' +
        'drawn at this height, whatever each one says for itself: a run of buttons at two different ' +
        'heights joined along one edge is a drawing mistake, not a layout.',
      default: 'md',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'What the run of buttons is, for a screen reader — "Text style", "Alignment". Without it the ' +
        'group is announced as an unnamed group of controls.',
    },
    {
      name: 'block',
      attribute: 'block',
      type: 'boolean',
      description:
        'Fill the width of whatever holds it, with the buttons sharing that width evenly rather than ' +
        'the longest label taking it.',
      default: false,
    },
  ],

  // The group itself raises nothing: a press is the button's own click, reaching the page through
  // the group the way any click reaches an ancestor. Saying so is the useful answer — a caller
  // looking for a group-level selection event should be told there isn't one rather than left to
  // discover it.
  events: [],

  state: [],

  slots: [
    {
      name: '',
      takes:
        'The buttons, in the order they are joined. Each keeps its own variant, its own pressed ' +
        'state and its own action; only the height is the group’s decision.',
    },
  ],

  tokens: [
    { token: '--caos-radius', controls: 'The corner radius of the two ends of the run. The corners between neighbours are squared.' },
    { token: '--caos-button-radius-start', controls: 'Set by the group on every button but the first, to square the corners it shares with the one before it. Not an input an author sets here.' },
    { token: '--caos-button-radius-end', controls: 'Set by the group on every button but the last, for the same reason at the other end.' },
  ],

  parts: [{ part: 'group', is: 'the run itself' }],

  methods: [],

  placing:
    'Place it where a single button would go, and put the buttons inside it. What each button DOES ' +
    'still comes from the action declared on that button’s placement — the group arranges and sizes, ' +
    'and decides nothing about behaviour.',

  whenToUse:
    'Several actions that belong to one another closely enough to read as one control: text styles, ' +
    'alignment, a view switcher, a run of toggles over the same thing. If the actions merely happen ' +
    'to sit near each other, they are a row of buttons and not a group.',

  insteadUse: [
    { name: 'ActionBar', when: 'A surface has several actions that are separate things, one of them primary, and the spare ones should collapse behind an overflow when it gets narrow.' },
    { name: 'SegmentedFilter', when: 'The run changes what is SHOWN rather than doing something, and exactly one of them is always in force.' },
    { name: 'OverflowMenu', when: 'The actions are secondary and belong behind a single trigger.' },
  ],

  accessibility:
    'The run is a named group, from label. It is deliberately NOT a toolbar: a toolbar promises ' +
    'arrow-key navigation between its controls, and these are ordinary buttons that Tab moves ' +
    'through one at a time — announcing otherwise would teach a keyboard user a key that does ' +
    'nothing. Whichever button is under the pointer or holding focus is drawn in front of its ' +
    'neighbours, so a focus ring is never clipped by the button beside it. An icon-only button in a ' +
    'group still needs its own label, exactly as it does anywhere else.',

  examples: [
    {
      key: 'actions',
      label: 'A joined run of actions',
      summary: 'Three actions over the same thing, read as one control rather than three.',
      placement: {
        component: 'caos-button-group',
        inputs: { label: 'Record actions' },
        children: [
          { component: 'caos-button', inputs: { variant: 'secondary' }, children: ['Edit'] },
          { component: 'caos-button', inputs: { variant: 'secondary' }, children: ['Clone'] },
          { component: 'caos-button', inputs: { variant: 'secondary' }, children: ['Share'] },
        ],
      },
    },
    {
      key: 'toggles',
      label: 'A run of toggles',
      summary:
        'Each button carries its own pressed state, so several can be on at once. Exclusivity is ' +
        'not something the group imposes — a run where exactly one is always in force is a ' +
        'segmented filter.',
      placement: {
        component: 'caos-button-group',
        inputs: { label: 'Text style' },
        children: [
          { component: 'caos-button', inputs: { icon: 'bold', label: 'Bold', pressed: 'true' } },
          { component: 'caos-button', inputs: { icon: 'italic', label: 'Italic', pressed: 'false' } },
          { component: 'caos-button', inputs: { icon: 'underline', label: 'Underline', pressed: 'false' } },
        ],
      },
    },
    {
      key: 'sizes',
      label: 'One height for the run',
      summary:
        'The group’s size, not each button’s. The middle button asks for a large one and is drawn ' +
        'at the group’s height anyway, which is what stops a run from arriving misaligned.',
      placement: {
        component: 'caos-button-group',
        inputs: { size: 'sm', label: 'View' },
        children: [
          { component: 'caos-button', inputs: { variant: 'secondary' }, children: ['List'] },
          { component: 'caos-button', inputs: { variant: 'secondary', size: 'lg' }, children: ['Board'] },
          { component: 'caos-button', inputs: { variant: 'secondary' }, children: ['Calendar'] },
        ],
      },
    },
  ],
};

export default docs;
